import { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '@/api/client';
import { cloudApi } from '@/api/cloud';

// 桌面端判定（preload 注入）：会员有效性 = 本机订阅 或 绑定的云端付费账号
const isDesktop = !!window.psbDesktop?.isDesktop;
// 付费档 slug 全家（09-06 三档同权：storage-6zhe/3zhe 与 storage 功能一致，只是不返 ¥3）
const PAID_TIERS = new Set(['storage', 'storage-6zhe', 'storage-3zhe', 'pro', 'team', 'enterprise']);

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description?: string;
  price_monthly: number;  // 分
  price_yearly: number;   // 分
  currency: string;
  features: Record<string, any>;
  limits: Record<string, any>;
  // 三档互斥（09-27）：storage 全家含停用档也下发，false=变暗不可选
  is_active?: boolean;
}

export interface Subscription {
  id: string;
  plan_id: string;
  plan_name?: string;
  status: 'active' | 'cancelled' | 'expired' | 'paused' | 'trial';
  billing_cycle: 'monthly' | 'yearly';
  price_paid: number;
  current_period_end?: string;
  auto_renew: boolean;
  trial_end?: string;
}

export interface SubscriptionState {
  plans: Plan[];
  currentSubscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  tier: 'free' | 'storage';
  features: Record<string, any>;
}

// ─── 订阅状态 Hook ───
export function useSubscription(): SubscriptionState & {
  subscribe: (planId: string, cycle: 'monthly' | 'yearly', payment_method?: string) => Promise<void>;
  cancel: (reason?: string) => Promise<void>;
  refresh: () => Promise<void>;
  checkFeature: (feature: string) => boolean;
  checkLimit: (key: string, current: number) => boolean;
} {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cloudPaid, setCloudPaid] = useState(false);

  // 桌面端：绑定的云端账号是付费会员时，会员同样生效（付费上云，本机不重复购买）
  useEffect(() => {
    if (!isDesktop) return;
    let cancelled = false;
    cloudApi.getStatus()
      .then((st) => {
        if (!cancelled) setCloudPaid(!!(st?.bound && st.account && PAID_TIERS.has(st.account.tier)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const localTier: 'free' | 'storage' = (currentSubscription?.status === 'active' || currentSubscription?.status === 'trial')
    ? (plans.find(p => p.id === currentSubscription.plan_id)?.slug as 'free' | 'storage' || 'free')
    : 'free';

  const tier: 'free' | 'storage' = localTier !== 'free' ? localTier : (cloudPaid ? 'storage' : 'free');

  const features = useMemo(() => (
    currentSubscription?.status === 'active' || currentSubscription?.status === 'trial'
      ? plans.find(p => p.id === currentSubscription.plan_id)?.features || {}
      : plans.find(p => p.slug === 'free')?.features || {}
  ), [currentSubscription, plans]);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/v1/billing/plans');
      setPlans(data);
    } catch (err: any) {
      console.warn('[Subscription] Failed to fetch plans:', err.message);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get('/api/v1/billing/subscription');
      setCurrentSubscription(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setCurrentSubscription(null);
      } else {
        setError(err.message || 'Failed to load subscription');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchPlans(), fetchSubscription()]);
  }, [fetchPlans, fetchSubscription]);

  const subscribe = useCallback(async (planId: string, cycle: 'monthly' | 'yearly', payment_method?: string) => {
    try {
      await apiClient.post('/api/v1/billing/subscribe', {
        plan_id: planId,
        billing_cycle: cycle,
        payment_method,
      });
      // 支付成功后获取完整订阅对象
      await fetchSubscription();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Subscribe failed');
      throw err;
    }
  }, [fetchSubscription]);

  const cancel = useCallback(async (reason?: string) => {
    try {
      await apiClient.delete('/api/v1/billing/subscription', { data: { reason } });
      await fetchSubscription();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Cancel failed');
      throw err;
    }
  }, [fetchSubscription]);

  const checkFeature = useCallback((feature: string): boolean => {
    if (!features) return false;
    return features[feature] === true;
  }, [features]);

  const checkLimit = useCallback((key: string, current: number): boolean => {
    const limits = plans.find(p => p.id === currentSubscription?.plan_id)?.limits || {};
    const limit = limits[key];
    if (limit === null || limit === undefined) return true; // 无限制
    return current < limit;
  }, [plans, currentSubscription]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    plans,
    currentSubscription,
    isLoading,
    error,
    tier,
    features,
    subscribe,
    cancel,
    refresh,
    checkFeature,
    checkLimit,
  };
}

